import {
  useCreateShare,
  useGetDetailShare,
  useGetShares,
  useUpdateShare,
} from '@vindr/services/actions/share.action';
import { useGetDetailStudy } from '@vindr/services/actions/study.action';
import { ACCESS_TYPE } from '@vindr/services/apis/share.api';
import { Share_Type } from '@vindr/services/constants/common.constant';
import { MESSAGE } from '@vindr/services/constants/message.constant';
import { base64ToBlob } from '@vindr/utils/converter';
import { addToArray, enumKeys } from '@vindr/utils/helpers/common.helper';
import cx from '@vindr/utils/helpers/cx';
import Password from '@vindr/utils/helpers/password';
import { generateQRCode } from '@vindr/utils/helpers/qrcode';
import useCopyToClipboard from '@vindr/utils/hooks/useCopyToClipboard';
import { displayText } from '@vindr/utils/index';
import { isMobile } from '@vindr/utils/mobile';
import { generateShareLink, generateVisitLink } from '@vindr/utils/report/generateLink';
import { Button, Form, Modal, Tabs, TabsProps } from 'antd';
import { MessageInstance } from 'antd/es/message/interface';
import { ModalStaticFunctions } from 'antd/es/modal/confirm';
import { FormInstance } from 'antd/lib/form';
import dayjs from 'dayjs';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Updater, useImmer } from 'use-immer';
import handleSave from '@vindr/ui/src/components/Templates/actionSubmit';
import { extendDateOptions } from '@vindr/ui/src/components/ui/DateRangePicker';
import Table from '@vindr/ui/src/components/ui/Table';
import FormItem from '@vindr/ui/src/components/common/FormItem/FormItem';
import LimitExceed from '@vindr/ui/src/components/common/LimitExceed';
import useDefinedSharedTableColumns from './useDefinedSharedTableColumns';
import { CopyOutlined } from '@ant-design/icons';

const currentTime = dayjs();

const ShareStudy = ({
  visible,
  study_id,
  visit_id,
  onCancel,
  stateAction = 'edit',
  message,
  modal,
  editItem,
}: ShareStudyInterface) => {
  const { t } = useTranslation('Vindoc');
  const [form] = Form.useForm();
  const [fromAllTime, toAllTime] = extendDateOptions['All Time'].map((date) => date.toISOString());
  const { data: responseStudy } = useGetDetailStudy(
    { study_id, study_date_from: fromAllTime, study_date_to: toAllTime },
    { enabled: !!study_id && stateAction === 'create' }
  );
  const studyInfo = responseStudy?.data;

  const { data: responseShare } = useGetDetailShare(
    {
      share_study_id: study_id,
      created_at_from: fromAllTime,
      created_at_to: toAllTime,
      type: editItem?.type,
      access_type: editItem?.access_type ?? ACCESS_TYPE.Study,
    },
    { enabled: !!study_id && stateAction !== 'create' }
  );
  const shareInfo =
    editItem ||
    responseShare?.data?.data?.find((it) => it?.id === editItem?.id) ||
    responseShare?.data?.data?.[0];

  const data: EditItem = stateAction === 'create' ? studyInfo : shareInfo;
  const isShareInfo = detectIsShareInfo(data);

  const [shareStudy, setShareStudy] = useImmer<ShareStudy>({
    editItem: data,
    okAction: form.submit,
    study_id,
    ...(isShareInfo && { accessKey: data?.access_key }),
  });

  useEffect(() => {
    setShareStudy((draft) => {
      draft.editItem = { ...data, visit_id: data?.visit_id ? data?.visit_id : visit_id };
      draft.stateAction = stateAction;
      draft.study_id = study_id;

      if (isShareInfo) draft.accessKey = data?.access_key;
    });
  }, [data, isShareInfo, setShareStudy, stateAction, study_id, visible, visit_id]);

  return (
    <Modal
      centered
      destroyOnClose
      forceRender
      maskClosable={false}
      open={visible}
      title={t('Study Share')}
      width={700}
      onCancel={onCancel}
      okText={!shareStudy?.accessKey ? t('Generate Link') : t('Save')}
      // okButtonProps={{ disabled: !shareStudy?.accessKey }}
      // bodyStyle={{ height: 680 }}
      onOk={form.submit}
    >
      <ShareStudyContext.Provider value={{ ...shareStudy, form, visible, setShareStudy }}>
        <ShareStudyChild {...{ modal, message, onCancel }} />
      </ShareStudyContext.Provider>
    </Modal>
  );
};

const ShareStudyChild = ({
  onCancel,
  message,
  modal,
}: Pick<ShareStudyInterface, 'onCancel' | 'message' | 'modal'>) => {
  const { editItem, stateAction, form, visible, accessKey } = useShareStudy();
  const isShareInfo = detectIsShareInfo(editItem);
  const access_type = Form.useWatch('access_type', form);
  const autoGeneratePassword = window.config?.share?.autoGenerate;

  const { t } = useTranslation('Vindoc');
  const [activeTab, setActiveTab] = useState('sharing');
  const { data: sharedList } = useGetShares(
    {
      limit: 100,
      offset: 0,
      institution_ids: addToArray(editItem?.institution_id),
      share_study_id: isShareInfo ? editItem?.share_study_id : editItem?.study_id,
      type: Share_Type.Guest,
      access_type: isShareInfo ? editItem?.access_type : ACCESS_TYPE.Study,
    },
    {
      enabled:
        !!editItem?.institution_id &&
        !!(isShareInfo ? editItem?.share_study_id : editItem?.study_id) &&
        visible,
    }
  );

  const isEdit = useMemo(() => stateAction === 'edit', [stateAction]);

  const editItemFormat: ParameterShare = useMemo(() => {
    const share_link =
      access_type === ACCESS_TYPE.Study
        ? generateShareLink(
            isShareInfo ? editItem?.share_study_instance_uid : '',
            accessKey,
            isShareInfo ? editItem?.modalities : editItem?.modality
          )
        : generateVisitLink({
            visit_id: editItem?.visit_id,
            access_key: accessKey,
          });

    if (isEdit && isShareInfo) {
      return {
        ...editItem,
        share_link,
      } satisfies ParameterEditShare;
    }

    return {
      type: Share_Type.Guest, //? Only create type Guest, type HIS only show
      expired_at: currentTime.add(30, 'day').toISOString(),
      access_type: access_type === undefined ? ACCESS_TYPE.Study : access_type,
      body_parts: !isShareInfo && editItem?.body_part,
      modalities: !isShareInfo && editItem?.modality,
      institution_id: editItem?.institution_id,
      patient_id: editItem?.patient_id,
      patient_name: editItem?.patient_name,
      share_study_id: !isShareInfo && editItem?.study_id,
      share_study_instance_uid: !isShareInfo && editItem?.study_instance_uid,
      share_link,
      note: !isShareInfo && displayText(editItem?.study_desc),
      password: autoGeneratePassword ? Password.generate() : '',
      visit_id: editItem?.visit_id,
    } satisfies ParameterCreateShare;
  }, [accessKey, access_type, editItem, isEdit, isShareInfo, autoGeneratePassword]);

  const onChangeTab = useCallback((key) => {
    setActiveTab(key);
  }, []);

  useEffect(() => {
    if (!visible) setActiveTab('sharing');
  }, [visible]);

  const { mutate: createShare } = useCreateShare();
  const { mutate: updateShare } = useUpdateShare();

  const items: TabsProps['items'] = [
    {
      key: 'sharing',
      label: t('Sharing'),
      children:
        sharedList?.data?.is_exceed_limit === true && !isEdit ? (
          <LimitExceed />
        ) : (
          <FormEdit
            editItem={editItemFormat}
            setCloseModal={onCancel}
            {...{
              createShare,
              message,
              updateShare,
            }}
          />
        ),
    },
    {
      key: 'shared',
      label: t('Shared'),
      children: <SharedTable editItem={editItem as any} {...{ onChangeTab, message, modal }} />,
    },
  ];

  return <Tabs activeKey={activeTab} items={items} onChange={onChangeTab} />;
};

const FormEdit = ({
  createShare,
  editItem,
  message,
  setCloseModal,
  updateShare,
}: {
  createShare: ReturnType<typeof useCreateShare>['mutate'];
  editItem: ParameterShare;
  message: ShareStudyInterface['message'];
  setCloseModal: ShareStudyInterface['onCancel'];
  updateShare: ReturnType<typeof useUpdateShare>['mutate'];
}) => {
  const { setShareStudy, stateAction, form } = useShareStudy();
  const { t } = useTranslation('Vindoc');
  const isEdit = detectEditShareInfo(stateAction, editItem);
  const access_type = Form.useWatch('access_type', form);

  useEffect(() => {
    form.resetFields();
    form.setFieldsValue(editItem);
  }, [editItem, isEdit]);

  const actionSave = () => {
    handleSave({
      message,
      form,
      type: stateAction,
      mutate: { create: createShare, update: updateShare },
      t,
      onDone: (val) => {
        if (stateAction === 'create') {
          setShareStudy((draft) => {
            draft.accessKey = val.data?.access_key;
            draft.stateAction = 'edit';
            draft.editItem = val.data;
          });
        } else {
          setCloseModal();
        }
      },
      editParams: (values: ParameterEditShare) => ({
        id: isEdit ? editItem?.id : '',
        data: {
          ...values,
          ...(detectEditShareInfo(stateAction, editItem) && {
            body_parts: editItem?.body_parts,
            modalities: editItem?.modalities,
            patient_id: editItem?.patient_id,
            institution_id: editItem?.institution_id,
            patient_name: editItem?.patient_name,
            share_study_id: editItem?.share_study_id,
            share_study_instance_uid: editItem?.share_study_instance_uid,
            ...(access_type === ACCESS_TYPE['Visit ID'] && {
              visit_id: editItem?.visit_id,
            }),
          }),
        } satisfies Omit<ParameterEditShare, 'share_link'>,
      }),
      createParams: (values) =>
        ({
          ...values,
          ...(detectCreateShareInfo(stateAction, editItem) && {
            body_parts: editItem?.body_parts,
            modalities: editItem?.modalities,
            patient_id: editItem?.patient_id,
            institution_id: editItem?.institution_id,
            patient_name: editItem?.patient_name,
            share_study_id: editItem?.share_study_id,
            share_study_instance_uid: editItem?.share_study_instance_uid,
            ...(access_type === ACCESS_TYPE['Visit ID'] && {
              visit_id: editItem?.visit_id,
            }),
          }),
        } satisfies Omit<ParameterCreateShare, 'share_link'>),
      code: { created: 'SCD_14001', updated: 'SCD_14002' },
    });
  };

  const onChangeDate = useCallback((e) => {
    const value = e.target.value;

    form.setFieldValue('expired_at', currentTime.add(value, 'day'));
  }, []);

  /*   const generateAccessKey = useCallback(() => {
    form.submit();
  }, [form]); */
  const handleCopy = useCopyToClipboard(message);
  const share_link = Form.useWatch('share_link', form);
  const password = Form.useWatch('password', form);
  const canCopy = useCallback(
    (callback: () => void) => {
      if (!share_link) {
        message.error(t('Generate link first!'));
        return;
      }
      callback();
    },
    [message, share_link, t]
  );

  const copyLink = useCallback(() => {
    canCopy(() =>
      handleCopy(share_link, { messages: { success: t('Link copied to clipboard.') } })
    );
  }, [canCopy, handleCopy, share_link, t]);

  const copyQRCode = useCallback(() => {
    canCopy(async () => {
      const qrBase64 = await generateQRCode(share_link);
      handleCopy(base64ToBlob(qrBase64), { type: 'image' });
    });
  }, [canCopy, handleCopy, share_link]);

  const copyPassword = useCallback(() => {
    canCopy(() =>
      handleCopy(password, { messages: { success: t('Password copied to clipboard.') } })
    );
  }, [canCopy, handleCopy, password, t]);

  const handleGeneratePassword = () => {
    form.setFieldValue('password', Password.generate());
  };

  return (
    <Form form={form} layout="vertical" name="edit-share" onFinish={actionSave}>
      <FormItem.Select
        disabled
        label={t('Share Type')}
        name="type"
        options={enumKeys(Share_Type).map((key) => ({ label: t(key), value: Share_Type[key] }))}
        rules={[{ required: true, message: t(MESSAGE.INLINE.IEM_0001) }]}
      />
      {isMobile() ? (
        <FormItem.DatePickerMobile
          showTime
          disabled={isEdit}
          label={t('Expired Date')}
          name="expired_at"
          rules={[{ required: true, message: t(MESSAGE.INLINE.IEM_0001) }]}
        />
      ) : (
        <FormItem.DatePicker
          showTime
          disabled={isEdit}
          label={t('Expired Date')}
          name="expired_at"
          rules={[{ required: true, message: t(MESSAGE.INLINE.IEM_0001) }]}
        />
      )}
      {dateOptions.map((date) => (
        <Button
          key={date}
          className="mb-6"
          onClick={() => onChangeDate({ target: { value: date } })}
        >
          {`${date} ${t('days')}`}
        </Button>
      ))}
      <FormItem.Input label={t('Note')} name="note" placeholder={t('Please enter note')} />
      <FormItem.Select
        disabled={isEdit}
        label={t('Access')}
        name="access_type"
        rules={[{ required: true, message: t(MESSAGE.INLINE.IEM_0001) }]}
        options={[
          { label: t('Selected study only'), value: ACCESS_TYPE.Study },
          { label: t('Visit ID'), value: ACCESS_TYPE['Visit ID'] },
        ]}
      />
      {!(editItem.type === Share_Type.HIS && editItem.access_type === ACCESS_TYPE.Study) && (
        <div className="flex max-sm:flex-col sm:space-x-2">
          <FormItem.Input
            className="flex-1"
            disabled={!window.config?.share?.canEdit}
            label={t('Password')}
            name="password"
            placeholder={t('Please enter password')}
          />
          <div className="space-x-2 max-sm:mb-7 max-sm:ml-0 sm:mt-7">
            {'access_key' in editItem && (
              <Button className="" onClick={copyPassword}>
                <CopyOutlined />
              </Button>
            )}
            <Button className="w-[140px]" onClick={handleGeneratePassword}>
              {t('Auto generate')}
            </Button>
          </div>
        </div>
      )}
      <div className="flex space-x-2">
        <FormItem.InputArea
          disabled
          formClassName="flex-1"
          label={t('Share Link')}
          name="share_link"
          placeholder={t('Share Link')}
          rows={4}
          style={{ resize: 'none' }}
        />
        <div
          className={cx('w-[140px] flex-col', {
            'flex': isEdit,
            'hidden': !isEdit,
          })}
        >
          <Button className="mt-7" onClick={copyLink}>
            {t('Copy Link')}
          </Button>
          <Button className="mt-7" onClick={copyQRCode}>
            {t('Copy QR Code')}
          </Button>
        </div>
      </div>
      {/* {!('access_key' in editItem) && (
        <Button onClick={generateAccessKey}>{t('Generate Link')}</Button>
      )} */}
    </Form>
  );
};

const SharedTable = ({
  message,
  modal,
  onChangeTab,
  editItem,
}: {
  message: ShareStudyInterface['message'];
  modal: ShareStudyInterface['modal'];
  onChangeTab: (key: any) => void;
  editItem: ShareInfo;
}) => {
  const { setShareStudy, visible, study_id } = useShareStudy();
  const [selectedRow, setSelectedRow] = useState([]);
  const columns = useDefinedSharedTableColumns({
    onChangeTab,
    setShareStudy,
    message,
    modal,
    setSelectedRow,
  });
  const { data: sharedList } = useGetShares(
    {
      limit: 100,
      offset: 0,
      institution_ids: addToArray(editItem?.institution_id),
      share_study_id: study_id,
      type: Share_Type.Guest,
      access_type: ACCESS_TYPE.Study,
    },
    { enabled: visible && !!study_id }
  );

  return (
    <Table
      className="h-[620px]"
      columns={columns}
      dataSource={sharedList?.data?.data || []}
      pagination={false}
      rowKey="id"
      scroll={{ y: 570 }}
      selectedRowKeys={selectedRow}
      setSelectedRowKeys={setSelectedRow}
      style={{ background: '#2c5166' }}
      wrapperClassName="h-auto"
    />
  );
};

const ShareStudyContext = createContext({});

export const useShareStudy = () => {
  const useShareStudyContext = useContext(ShareStudyContext);

  if (!useShareStudyContext) {
    throw new Error('useShareStudy must be used within a ShareStudyProvider');
  }

  return useShareStudyContext as {
    editItem: EditItem;
    stateAction: StateAction;
    form: FormInstance;
    visible: boolean;
    accessKey: ShareStudy['accessKey'];
    shareStudy: ShareStudy;
    setShareStudy: Updater<ShareStudy>;
    study_id: string;
  };
};

const dateOptions = [7, 14, 30];

export default ShareStudy;

function detectIsShareInfo(editItem: EditItem): editItem is ShareInfo {
  return editItem && 'access_key' in editItem;
}

function detectEditShareInfo(
  stateAction: StateAction,
  editItem: ParameterShare
): editItem is ParameterEditShare {
  return stateAction === 'edit';
}

function detectCreateShareInfo(
  stateAction: StateAction,
  editItem: ParameterShare
): editItem is ParameterCreateShare {
  return stateAction === 'create';
}

type StudyInfo = ReturnType<typeof useGetDetailStudy>['data']['data'][number];
type ShareInfo = ReturnType<typeof useGetDetailShare>['data']['data']['data'][number];

type EditItem = StudyInfo | ShareInfo;

type ShareStudy = {
  stateAction?: StateAction;
  editItem?: EditItem;
  okAction: () => void;
  accessKey?: ShareInfo['access_key'];
  study_id: string;
};

type StateAction = 'create' | 'edit' | 'delete';

type ShareLink = { share_link: string };
type ParameterCreateShare = Parameters<ReturnType<typeof useCreateShare>['mutate']>['0'] &
  ShareLink;
type ParameterEditShare = Parameters<ReturnType<typeof useUpdateShare>['mutate']>['0']['data'] &
  ShareLink &
  ShareInfo;

type ParameterShare = ParameterCreateShare | ParameterEditShare;

interface CommonShareStudyInterface {
  visible: boolean;
  study_id: string;
  visit_id: string;
  onCancel: () => void;
  message: MessageInstance;
  modal: Omit<ModalStaticFunctions, 'warn'>;
}

interface CreateShareStudyInterface extends CommonShareStudyInterface {
  stateAction: 'create';
  editItem?: ReturnType<typeof useGetDetailShare>['data']['data']['data'][number];
}

interface EditDeleteShareStudyInterface extends CommonShareStudyInterface {
  stateAction: 'edit' | 'delete';
  editItem: ReturnType<typeof useGetDetailShare>['data']['data']['data'][number];
}

type ShareStudyInterface = CreateShareStudyInterface | EditDeleteShareStudyInterface; //? Need add access_type when edit share
